import { useEffect, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';

import { getOrCreateSplitKey } from 'src/modules/app/components/Providers/Telemetry/Split';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import { useCurrentPage } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { sessionStarted } from 'src/shared/telemetry';
import { isBrowser } from 'src/shared/utils/environment';

const SESSION_KEY = '_hover-user-client-session';
const DEFAULT_SESSION_EXPIRATION = 60 * 30 * 1000;

type UserSession = {
  sessionId: string;
  expiresAt: string;
};

type SessionSetter = (userSessionId: string) => void;

const getExpiresAt = () =>
  new Date(Date.now() + DEFAULT_SESSION_EXPIRATION).toISOString();

/**
 * Creates a new session object and stores it in the local storage.
 * @returns The newly created session object.
 */
const createNewSession = (page: string, userId?: number) => {
  const newUserSession: UserSession = {
    sessionId: uuidV4(),
    expiresAt: getExpiresAt(),
  };

  sessionStarted({
    feature: FeatureTracking.HOMEOWNER_APP,
    page_or_screen_name: page,
    session_id: newUserSession.sessionId,
    user_id: userId,
    split_key: isBrowser() ? getOrCreateSplitKey() : '',
  });

  localStorage.setItem(SESSION_KEY, JSON.stringify(newUserSession));
  return newUserSession;
};

const getUserSession = () => {
  const userSessionJSON = localStorage.getItem(SESSION_KEY);
  return userSessionJSON ? (JSON.parse(userSessionJSON) as UserSession) : null;
};

/**
 * In certain cases, we want to manually set the session id, for example, when moving
 * from an embed to the homeowner app. This ensures a series of tracking events
 * can be linked cross domain.
 * @param sessionId - The session id to store in the local storage.
 */
const setUserSession = (sessionId: string) => {
  const userSession: UserSession = {
    sessionId,
    expiresAt: getExpiresAt(),
  };

  localStorage.setItem(SESSION_KEY, JSON.stringify(userSession));
};

const clearUserSession = () => localStorage.removeItem(SESSION_KEY);

const sessionExpired = (userSession: UserSession) =>
  userSession.expiresAt < new Date().toISOString();

/**
 * Factory function that creates a session activity listener.
 * The listener checks the user session and updates it if necessary.
 * @param sessionSetter - A function to set the session object.
 * @returns A function that can be used as a session activity listener.
 */
const createSessionActivityListener =
  (sessionSetter: SessionSetter, page: string, userId?: number) => () => {
    const userSession = getUserSession();

    if (!userSession || sessionExpired(userSession)) {
      return sessionSetter(createNewSession(page, userId).sessionId);
    }

    const updatedUserSession: UserSession = {
      sessionId: userSession.sessionId,
      expiresAt: getExpiresAt(),
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(updatedUserSession));
    sessionSetter(updatedUserSession.sessionId);

    return updatedUserSession;
  };

type UseUserSessionProps = {
  userId?: number;
};

/**
 * Custom hook that manages the user session in a React component.
 *
 * @returns The current user session id or null if no session is found.
 */
const useUserSession = ({ userId }: UseUserSessionProps) => {
  const { mergeMetadata } = useTrackingMetadata();
  const defaultSessionId = isBrowser()
    ? getUserSession()?.sessionId
    : undefined;
  const page = useCurrentPage() || '';
  const [userSessionId, setUserSessionId] = useState<string | undefined>(
    defaultSessionId,
  );
  useEffect(() => {
    mergeMetadata({ session_id: userSessionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSessionId]);

  useEffect(() => {
    const sessionActivityListener = createSessionActivityListener(
      setUserSessionId,
      page,
      userId,
    );
    window.addEventListener('mousedown', sessionActivityListener);
    window.addEventListener('keydown', sessionActivityListener);
    window.addEventListener('wheel', sessionActivityListener);
    window.addEventListener('touchstart', sessionActivityListener);

    return () => {
      window.removeEventListener('mousedown', sessionActivityListener);
      window.removeEventListener('keydown', sessionActivityListener);
      window.removeEventListener('wheel', sessionActivityListener);
      window.removeEventListener('touchstart', sessionActivityListener);
    };
  }, [page, userId]);

  useEffect(() => {
    const session = getUserSession();

    if (!session || sessionExpired(session)) {
      // Send `Session Started` event on new sessions
      const { sessionId } = createNewSession(page, userId);

      setUserSessionId(sessionId);
      return;
    }

    setUserSessionId(session.sessionId);
  }, [page, userId]);

  return userSessionId;
};

export {
  useUserSession,
  DEFAULT_SESSION_EXPIRATION,
  SESSION_KEY,
  getExpiresAt,
  clearUserSession,
  UserSession,
  getUserSession,
  setUserSession,
};
